import React, { useCallback, useState } from 'react';

import BaseModal from '../components/_molecules/BaseModal';
import { FeaturePaywall } from '../components/_molecules/FeaturePaywall';
import {
  TexasSchoolPlanPaywall,
  CaliforniaSchoolPlanPaywall,
} from '../components/_organisms/StateSpecificTestPrepPaywall';
import useAccessBadge from './useAccessBadge';
import { useMetrics } from './useMetrics';

type PaywallVariant = 'default' | 'TX' | 'CA';

function isPaywallVariant(value: string | undefined): value is PaywallVariant {
  return value === 'TX' || value === 'CA';
}

function usePaywallPrompt({
  startState,
  onClose,
}: {
  startState?: { screen: string };
  onClose?: () => void;
}) {
  const { accessBadge: userAccessBadge } = useAccessBadge();
  const metrics = useMetrics();
  const [show, setShow] = useState(false);
  const [variant, setVariant] = useState<PaywallVariant>('default');

  const isReady = userAccessBadge !== undefined;

  const shouldPromptPaywall = useCallback(() => {
    return false; // Simplified - no longer prompting automatically
  }, []);

  const prompt = useCallback(
    (metadata?: PaywallMetadata) => {
      const paywallVariant = metadata?.variant;
      const derivedVariant =
        paywallVariant && isPaywallVariant(paywallVariant) ? paywallVariant : 'default';

      metrics.logEvent('Paywall.Prompted', {
        ...metadata,
        screen: startState?.screen,
        state: derivedVariant,
      });
      setVariant(derivedVariant);
      setShow(true);
    },
    [metrics, startState?.screen],
  );

  const hide = useCallback(() => {
    setShow(false);
    setVariant('default');
    onClose && onClose();
  }, [onClose]);

  const region = userAccessBadge?.organization?.region;
  const country = userAccessBadge?.organization?.country;

  const renderPaywall = () => {
    switch (variant) {
      case 'TX':
        return (
          <BaseModal width="1000px" title="" open={show} onCancel={hide} style={{ top: 10 }}>
            <TexasSchoolPlanPaywall />
          </BaseModal>
        );
      case 'CA':
        return (
          <BaseModal width="1000px" title="" open={show} onCancel={hide} style={{ top: 10 }}>
            <CaliforniaSchoolPlanPaywall />
          </BaseModal>
        );
      case 'default':
      default:
        return (
          <FeaturePaywall
            visible={show}
            onClose={hide}
            region={region}
            country={country}
          />
        );
    }
  };

  const component = renderPaywall();

  return {
    promptPaywall: (metadata?: PaywallMetadata) => prompt(metadata),
    hidePaywall: hide,
    paywallComponent: component,
    shouldPromptPaywall,
    isReady,
    isShowingPaywallPrompt: show,
    userAccessBadge,
    tenantName: userAccessBadge?.organization?.name,
  };
}

export type PaywallMetadata = {
  label?: string;
  description?: string;
  screen?: string;
  variant?: string;
};

export default usePaywallPrompt;
